import { Link } from "react-router-dom";
import { Nav } from "@/components/landing/Nav";
import { Footer } from "@/components/landing/Footer";
import { SEO } from "@/components/SEO";

const Section = ({ title, children, id }) => (
  <section id={id} className="mb-12" data-testid={`legal-section-${id}`}>
    <h2 className="font-display text-2xl md:text-[28px] tracking-tight text-ink mb-5">
      {title}
    </h2>
    <div className="space-y-4 text-[15px] text-slate-700 leading-relaxed">
      {children}
    </div>
  </section>
);

export default function TermsPage() {
  const lastUpdated = "May 2026";

  return (
    <main className="bg-white text-ink min-h-screen" data-testid="terms-page">
      <SEO
        title="Terms of Use — Gradatim"
        description="Terms governing your access to and use of the Gradatim website and platform."
        path="/terms"
      />
      <Nav />

      <section className="bg-cream border-b border-hairline">
        <div className="max-w-[840px] mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-14 md:pb-20">
          <div className="overline text-copper mb-4">Legal</div>
          <h1 className="font-display font-medium text-4xl md:text-[52px] tracking-tight leading-[1.05] text-ink">
            Terms of Use
          </h1>
          <p className="text-[14px] text-slate-500 mt-5">
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-[840px] mx-auto px-6 md:px-10 py-16 md:py-20">
          <Section id="acceptance" title="1. Acceptance of terms">
            <p>
              These Terms of Use ("Terms") govern your access to and use of the
              website operated by{" "}
              <strong>Gradatim IT Ventures India Private Limited</strong>{" "}
              ("Gradatim", "we", "us", or "our") at{" "}
              <Link to="/" className="text-copper hover:underline">
                www.gradatim.co.in
              </Link>
              . By accessing or using the site, you agree to be bound by these
              Terms. If you do not agree, please do not use the site.
            </p>
          </Section>

          <Section id="use-of-site" title="2. Use of the site">
            <p>
              This site is intended to provide information about Gradatim's
              products and services, including MFInsure® Core, MFInsure® GRIP,
              Gradatim Intelligence® and related offerings. You agree to use
              the site only for lawful purposes and in a manner that does not
              infringe the rights of, or restrict the use of, the site by any
              third party.
            </p>
          </Section>

          <Section id="intellectual-property" title="3. Intellectual property">
            <p>
              All content on this site — including text, graphics, logos,
              trademarks, product names (including "Gradatim", "MFInsure®" and
              "Gradatim Intelligence®"), software and design — is the property
              of Gradatim or its licensors and is protected by Indian and
              international intellectual property laws.
            </p>
            <p>
              You may not reproduce, modify, distribute or republish any
              materials from this site without our prior written permission.
            </p>
          </Section>

          <Section id="no-warranties" title="4. Disclaimer of warranties">
            <p>
              The information on this site is provided on an "as is" and "as
              available" basis. While we strive for accuracy, Gradatim makes no
              warranties, express or implied, regarding the completeness,
              reliability or accuracy of the content.
            </p>
            <p>
              Case study metrics, capability descriptions and outcome figures
              are illustrative and represent results delivered for specific
              clients; individual results may vary.
            </p>
          </Section>

          <Section id="liability" title="5. Limitation of liability">
            <p>
              To the maximum extent permitted by law, Gradatim shall not be
              liable for any indirect, incidental, consequential, special or
              exemplary damages arising out of or in connection with your use
              of the site, including (without limitation) loss of profits, data
              or business interruption.
            </p>
          </Section>

          <Section id="third-party" title="6. Third-party content and links">
            <p>
              Our site may contain links to third-party websites or services
              that are not owned or controlled by Gradatim. We have no control
              over and assume no responsibility for the content, privacy
              practices or policies of any third-party websites.
            </p>
          </Section>

          <Section id="privacy" title="7. Privacy">
            <p>
              Your use of the site is also governed by our{" "}
              <Link to="/privacy" className="text-copper hover:underline">
                Privacy Policy
              </Link>
              , which explains how we collect and use your information.
            </p>
          </Section>

          <Section id="changes" title="8. Changes to the terms">
            <p>
              We may revise these Terms from time to time. Any changes will be
              posted on this page with an updated "Last updated" date. Your
              continued use of the site after changes are posted constitutes
              your acceptance of the revised Terms.
            </p>
          </Section>

          <Section id="governing-law" title="9. Governing law and jurisdiction">
            <p>
              These Terms are governed by the laws of India. Any dispute
              arising out of or in connection with these Terms or your use of
              the site shall be subject to the exclusive jurisdiction of the
              competent courts in India.
            </p>
          </Section>

          <Section id="contact" title="10. Contact us">
            <p>
              For questions about these Terms, please contact:
              <br />
              <br />
              <strong>Gradatim IT Ventures India Private Limited</strong>
              <br />
              India
              <br />
              Email:{" "}
              <a href="mailto:innovate@gradatim.co.in" className="text-copper hover:underline">
                innovate@gradatim.co.in
              </a>
            </p>
          </Section>
        </div>
      </section>

      <Footer />
    </main>
  );
}
